import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    useTheme,
    Paper,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    List,
    ListItem,
    ListItemText,
    IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import API from "../api/axiosInstance";
import SaveStatusDialog from "./SaveStatusDialog";
import { Group, RecordCreationResults, User } from "../types/interfaces";
import { isArray } from "util";

const GroupsPanel: React.FC = () => {
    const theme = useTheme();
    const gridRef = useRef<any>(null);

    const [rowData, setRowData] = useState<Group[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [editingGroup, setEditingGroup] = useState<Group | null>(null);
    const [groupUsers, setGroupUsers] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [saveStatus, setSaveStatus] = useState<"saving" | "success" | "error" | null>(null);
    const [errorMessage, setErrorMessage] = useState("");

    const headers = () => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        return {
            headers: {
                authToken: user?.user?.authtoken || "",
                sessionId: user?.user?.sessionId || ""
            }
        };
    };

    useEffect(() => {
        fetchGroups();
        fetchAllUsers();
    }, []);

    const fetchGroups = async () => {
        try {
            const res = await API.get("/security/groups", headers());
            setRowData(res.data || []);
        } catch (err) {
            console.error("Failed to fetch groups", err);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const res = await API.get("/security/users", headers());
            setAllUsers(res.data || []);
        } catch (err) {
            console.error("Failed to fetch users", err);
        }
    };

    const fetchGroupMembers = async (groupId: string) => {
        try {
            const res = await API.get(`/security/groups/${groupId}/members`, headers());
            setGroupUsers(res.data || []);
        } catch (err) {
            console.error("Failed to fetch group members", err);
        }
    };

    const handleAdd = () => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        setEditingGroup({
            groupID: "",
            groupName: "",
            description: "",
            tenantID: "",
            canCreateObjects: false,
            canModifyObjects: false,
            canDeleteObjects: false,
            deleted: false,
            createdAt: user?.user?.userID || ""
        });
        setGroupUsers([]);
        setIsDialogOpen(true);
    };

    const handleEdit = async (group: Group) => {
        setEditingGroup(group);
        await fetchGroupMembers(group.groupID);
        setIsDialogOpen(true);
    };

    const handleRowDoubleClick = async (event: any) => {
        if (event?.data) {
            await handleEdit(event.data);
        }
    };

    const handleDelete = async (group: Group) => {
        if (!window.confirm(`Are you sure you want to delete "${group.groupName}"?`)) return;
        try {
            setSaveStatus("saving");
            await API.delete(`/security/groups/${group.groupID}`, headers());
            await fetchGroups();
            setSaveStatus("success");
        } catch (err: any) {
            console.error(err);
            setErrorMessage(err?.response?.data?.message || "Failed to delete group.");
            setSaveStatus("error");
        }
    };

    const handleSave = async () => {
        if (!editingGroup?.groupName.trim()) {
            setErrorMessage("Group name is required.");
            setSaveStatus("error");
            return;
        }
    
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const tenantID = user?.user?.tenantID || "";
        const userID = user?.user?.userID || "";
    
        try {
            setSaveStatus("saving");
            let groupId = editingGroup.groupID;
    
            if (!groupId) {
                // 🔄 Create Group via GroupCreationRequest
                const createPayload = {
                    tenantID,
                    groupName: editingGroup.groupName.trim(),
                    description: editingGroup.description || "",
                    canCreateObjects: editingGroup.canCreateObjects || false,
                    canModifyObjects: editingGroup.canModifyObjects || false,
                    canDeleteObjects: editingGroup.canDeleteObjects || false,
                };
    
                const res = await API.post("/security/groups", createPayload, headers());
                const result: RecordCreationResults = res.data;
    
                if (!result.wasSuccessful || result.newRecordIDs.length === 0) {
                    setSaveStatus("error");
                    setErrorMessage("Failed to create group.");
                    return;
                }
    
                groupId = result.newRecordIDs[0];
            } else {
                // ✏️ Update group normally
                const updatePayload = {
                    ...editingGroup,
                    tenantID,
                    updatedBy: userID
                };
                await API.put(`/security/groups/${groupId}`, updatePayload, headers());
            }
    
            // 👥 Sync Users in group
            const currentRes = await API.get(`/security/groups/${groupId}/members`, headers());
            const currentIds = currentRes.data?.map((u: User) => u.userID) || [];
            const selectedIds = groupUsers.map(u => u.userID);
    
            const toAdd = selectedIds.filter(id => !currentIds.includes(id));
            const toRemove = currentIds.filter((id: string) => !selectedIds.includes(id));
    
            await Promise.all([
                ...toAdd.map(uid => API.post(`/security/groups/${groupId}/members/${uid}`, {}, headers())),
                ...toRemove.map((uid: any) => API.delete(`/security/groups/${groupId}/members/${uid}`, headers()))
            ]);
    
            await fetchGroups();
            setSaveStatus("success");
            setTimeout(() => {
                setSaveStatus(null);
                setIsDialogOpen(false);
            }, 1000);
        } catch (err: any) {
            console.error(err);
            setErrorMessage(err?.response?.data?.message || "Failed to save group.");
            setSaveStatus("error");
        }
    };
    

    const handleAddUserToGroup = () => {
        const user = allUsers.find((u) => u.userID === selectedUserId);
        if (user && !groupUsers.some((u) => u.userID === user.userID)) {
            setGroupUsers((prev) => [...prev, user]);
            setSelectedUserId("");
        }
    };

    const handleRemoveUser = (userId: string) => {
        setGroupUsers((prev) => prev.filter((u) => u.userID !== userId));
    };

    const columnDefs: ColDef[] = [
        { field: "groupName", headerName: "Group Name", flex: 1, sortable: true },
        { field: "description", headerName: "Description", flex: 2, sortable: true },
        {
            headerName: "Actions",
            width: 200,
            cellRenderer: (params: { data: Group; }) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => handleEdit(params.data)}>
                        Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.data)}>
                        Delete
                    </Button>
                </Stack>
            )
        }
    ];

    return (
        <Box>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Groups</Typography>
                <Button variant="contained" onClick={handleAdd}>+ Add Group</Button>
            </Box>

            <Paper elevation={2} sx={{ p: 1, borderRadius: 2, backgroundColor: theme.palette.background.paper }}>
                {rowData.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, fontStyle: 'italic', textAlign: 'center', color: 'text.secondary' }}>
                        No groups found.
                    </Typography>
                ) : (
                    <Box
                        className={`ag-theme-${theme.palette.mode === "dark" ? "alpine-dark" : "alpine"}`}
                        sx={{ height: "60vh", width: "100%" }}
                    >
                        {columnDefs && rowData && (
                        <AgGridReact
                        ref={gridRef}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        rowSelection="single"
                        onRowDoubleClicked={handleRowDoubleClick}
                    />                            
                        )}

                    </Box>
                )}
            </Paper>

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingGroup?.groupID ? "Edit Group" : "Add Group"}</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Group Name"
                        value={editingGroup?.groupName || ""}
                        onChange={(e) => setEditingGroup((prev) => ({ ...prev!, groupName: e.target.value }))}
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Description"
                        value={editingGroup?.description || ""}
                        onChange={(e) => setEditingGroup((prev) => ({ ...prev!, description: e.target.value }))}
                        fullWidth
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                    />

                    <Typography variant="subtitle1" sx={{ mt: 2 }}>Group Members</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1 }}>
                        <Select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem value="" disabled>Select user to add</MenuItem>
                            {allUsers.map((user) => (
                                <MenuItem key={user.userID} value={user.userID}>
                                    {user.fullName || user.username}
                                </MenuItem>
                            ))}
                        </Select>
                        <Button variant="outlined" onClick={handleAddUserToGroup}>Add</Button>
                    </Stack>

                    {groupUsers.length === 0 ? (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', textAlign: 'center', color: 'text.secondary' }}>
                            No users in group.
                        </Typography>
                    ) : (
                        <List dense>
                            {groupUsers.map((user) => (
                                <ListItem
                                    key={user.userID}
                                    secondaryAction={
                                        <IconButton edge="end" onClick={() => handleRemoveUser(user.userID || "")}>
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText
                                        primary={user.fullName || user.username}
                                        secondary={user.email}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>

            {saveStatus && (
                <SaveStatusDialog
                    open={true}
                    status={saveStatus}
                    message={errorMessage}
                    onClose={() => setSaveStatus(null)}
                />
            )}
        </Box>
    );
};

export default GroupsPanel;
